import React from "react"
import {
  Box, Flex, Heading, Text, VStack,
} from "@chakra-ui/react"
import sortBy from "lodash/sortBy"
import { AgendaRow, Speaker, Website } from "sharedTypes"
import { formatInTimeZone } from "date-fns-tz"
import Button from "components/Buttons/Button"
import SpeakerDisplay from "components/elements/SpeakerDisplay"
import SectionHeader from "./SectionHeader"
import SpeakerModal from "../components/SpeakerModal/SpeakerModal"
import RichDescription from "../RichDescription"

const Agenda = ({
  agendaRows,
  speakers,
  website,
  onEditAgendaItem,
  onEditAgendaGroup,
  onAddFirstEntry,
}: {
  agendaRows: AgendaRow[]
  speakers: Speaker[]
  website?: Website
  onEditAgendaItem?: (agendaItem: AgendaRow) => void
  onEditAgendaGroup?: (agendaGroup: AgendaRow) => void
  onAddFirstEntry?: () => void
}) => {
  const agendaEnabled = !website || website.agendaEnabled !== false
  const headerText = website?.agendaHeader || "Agenda"

  const sortedAgendaRows = sortBy(agendaRows, [
    (row) => {
      const t = new Date(row.startsAt).getTime()

      return Number.isNaN(t) ? Infinity : t
    },
    (row) => (row.type === "AgendaHeading" ? 0 : 1),
  ])

  let content: React.ReactNode
  if (!agendaRows.length) {
    if (!onAddFirstEntry) {
      content = <SectionHeader text={headerText} />
    } else {
      content = (
        <>
          <SectionHeader text={headerText} />
          <Box textAlign="center" mb={16} mt={20} px={14}>
            <Text color="gray.600" mb={4}>
              There are no agenda entries yet.
            </Text>
            <Button size="sm" variant="outline" leftIcon="plus" onClick={onAddFirstEntry}>
              Add first entry
            </Button>
          </Box>
        </>
      )
    }
  } else {
    content = (
      <>
        <SectionHeader text={headerText} />
        <Box px={14}>
          {sortedAgendaRows.flatMap((agendaRow, index) => {
            const isLast = index === sortedAgendaRows.length - 1
            const row = (() => {
              switch (agendaRow.type) {
                case "AgendaHeading":
                  return (
                    <AgendaHeading
                      agendaRow={agendaRow}
                      onEdit={onEditAgendaGroup}
                      key={index}
                    />
                  )
                case "AgendaItem":
                  return (
                    <AgendaItem
                      agendaRow={agendaRow}
                      speakers={speakers}
                      onEdit={onEditAgendaItem}
                      key={index}
                    />
                  )
                default:
                  return null
              }
            })()
            const divider = !isLast && row && agendaRow.type !== "AgendaHeading" ? (
              <Flex key={`divider-${index}`} my={12}>
                <Box w={1 / 3} />
                <Box w={2 / 3} borderTopWidth={1} borderColor="gray.200" />
              </Flex>
            ) : null

            return divider ? [row, divider] : [row]
          })}
        </Box>
      </>
    )
  }

  return (
    <Box opacity={agendaEnabled ? 1 : 0.5} transition="opacity 0.2s" pb={14}>
      {content}
    </Box>
  )
}

const AgendaHeading = ({
  agendaRow: { title, description },
  agendaRow,
  onEdit,
}: {
  agendaRow: AgendaRow
  onEdit?: (agendaGroup: AgendaRow) => void
}) => (
  <Flex mb={14} pl={3} gap={3} alignItems="flex-start">
    <Box flex={1} minW={0}>
      <Heading mb={3} fontSize="2xl">
        {title}
      </Heading>
      <RichDescription content={description} />
    </Box>
    {onEdit && (
      <Button
        size="xs"
        variant="outline"
        flexShrink={0}
        onClick={(e) => {
          e.stopPropagation()
          onEdit(agendaRow)
        }}
      >
        Edit
      </Button>
    )}
  </Flex>
)

const AgendaItem = ({
  agendaRow: {
    startsAt, endsAt, title, description, speakerIds,
  },
  agendaRow,
  speakers,
  onEdit,
}: {
  agendaRow: AgendaRow
  speakers: Speaker[]
  onEdit?: (agendaItem: AgendaRow) => void
}) => {
  const timeLabel = () => {
    if (!startsAt) return ""

    const startDate = new Date(startsAt)
    if (Number.isNaN(startDate.getTime())) return ""

    if (endsAt) {
      const endDate = new Date(endsAt)
      if (Number.isNaN(endDate.getTime())) {
        return formatInTimeZone(startDate, "UTC", "p")
      }

      return [
        formatInTimeZone(startDate, "UTC", "p"),
        formatInTimeZone(endDate, "UTC", "p"),
      ].join(" — ")
    }

    return formatInTimeZone(startDate, "UTC", "p")
  }

  return (
    <Flex>
      <Box w={1 / 3} pl={3}>
        <Text fontWeight="bold" color="gray.500">
          {timeLabel()}
        </Text>
      </Box>
      <Flex w={2 / 3} gap={3} alignItems="flex-start">
        <VStack align="stretch" spacing={4} flex={1} minW={0}>
          <Heading fontSize="lg" wordBreak="break-word">
            {title}
          </Heading>
          <RichDescription content={description} />
          {speakerIds.map((speakerId) => {
            const speaker = speakers.find((s) => s.id === speakerId)
            if (speaker) {
              return (
                <SpeakerModal
                  key={speakerId}
                  speaker={speaker}
                  trigger={<SpeakerDisplay {...speaker} />}
                />
              )
            }

            return null
          })}
        </VStack>
        {onEdit && (
          <Button
            size="xs"
            variant="outline"
            flexShrink={0}
            onClick={(e) => {
              e.stopPropagation()
              onEdit(agendaRow)
            }}
          >
            Edit
          </Button>
        )}
      </Flex>
    </Flex>
  )
}

export default Agenda
